import type { ColumnDef, Row } from "@tanstack/react-table";
import * as React from "react";
import { useAction, useQuery } from "convex/react";
import { AlertTriangle, DollarSign, Mail, Package, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { BADGE_TONES } from "@/lib/badge-tones";
import { api } from "@/lib/convexApi";
import { formatCurrency, formatDate } from "@/lib/format";
import { hasPermission } from "@/lib/roles";

type DateRangeValue = "30d" | "90d" | "6mo" | "1y" | "all";

type SalesClientPerformanceRow = {
  id: string;
  userProfileId: string;
  clientName: string;
  company: string | null;
  email: string | null;
  tier: number | null;
  isEnterprise: boolean;
  totalRevenue: number;
  totalBags: number;
  totalOrders: number;
  avgOrderValue: number;
  last30Revenue: number;
  previous30Revenue: number;
  revenueMoMPercent: number | null;
  avgMonthlyRevenue: number;
  lastOrderAt: number;
  // Payment health
  unpaidAmount: number;
  overdueCount: number;
  failedCount: number;
  oldestOverdueDays: number | null;
  failureReasons: string[];
};

type SalesClientPerformanceResult = {
  salesRepOptions?: Array<{
    value: string;
    label: string;
    clientCount: number;
  }>;
  selectedSalesRepId?: string | null;
  totals: {
    clientCount: number;
    assignedClientCount: number;
    orderCount: number;
    totalBags: number;
    totalRevenue: number;
    last30Revenue: number;
    previous30Revenue: number;
    avgMonthlyRevenue: number;
    revenueMoMPercent: number | null;
    totalUnpaidAmount: number;
    totalUnpaidClients: number;
  };
  data: SalesClientPerformanceRow[];
};

type ClientOrderRow = {
  _id: string;
  orderNumber: string | null;
  status: string;
  createdAt: number;
  orderTotal: number;
  itemCount: number;
  paymentMethod: string | null;
  paymentStatus: string | null;
  paymentTerms: string | null;
  dueDate: number | null;
  paymentHealth: "paid" | "pending" | "overdue" | "failed";
  unpaidAmount: number;
  failedCount: number;
  overdueCount: number;
  oldestOverdueDays: number | null;
  failureReasons: string[];
};

const DATE_RANGE_OPTIONS: Array<{ value: DateRangeValue; label: string }> = [
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "6mo", label: "6 months" },
  { value: "1y", label: "1 year" },
  { value: "all", label: "All time" },
];

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function tierBadgeClass(tier: number | null): string {
  if (!tier) return BADGE_TONES.slate;
  if (tier <= 1) return BADGE_TONES.amber;
  if (tier === 2) return BADGE_TONES.blue;
  if (tier >= 3) return BADGE_TONES.violet;
  return BADGE_TONES.slate;
}

function momentumBadgeClass(value: number | null): string {
  if (value === null) return BADGE_TONES.slate;
  if (value >= 20) return BADGE_TONES.emerald;
  if (value >= 0) return BADGE_TONES.sky;
  if (value <= -20) return BADGE_TONES.red;
  return BADGE_TONES.amber;
}

function paymentHealthBadgeClass(
  row: SalesClientPerformanceRow,
): { className: string; label: string } {
  if (row.failedCount > 0) {
    return { className: BADGE_TONES.red, label: "Failed" };
  }
  if (row.overdueCount > 0) {
    return { className: BADGE_TONES.amber, label: "Overdue" };
  }
  return { className: BADGE_TONES.emerald, label: "Current" };
}

function orderPaymentHealthBadge(health: string): { className: string; label: string } {
  switch (health) {
    case "failed":
      return { className: BADGE_TONES.red, label: "Failed" };
    case "overdue":
      return { className: BADGE_TONES.amber, label: "Overdue" };
    case "pending":
      return { className: BADGE_TONES.sky, label: "Pending" };
    case "paid":
    default:
      return { className: BADGE_TONES.emerald, label: "Paid" };
  }
}

function orderStatusBadge(status: string): { className: string; label: string } {
  switch (status) {
    case "awaiting_inventory":
      return { className: BADGE_TONES.amber, label: "Awaiting Inventory" };
    case "ready_to_prepare":
      return { className: BADGE_TONES.sky, label: "Ready to Prepare" };
    case "ready_for_pickup":
      return { className: BADGE_TONES.blue, label: "Ready for Pickup" };
    case "in_transit":
      return { className: BADGE_TONES.violet, label: "In Transit" };
    case "delivered":
      return { className: BADGE_TONES.emerald, label: "Delivered" };
    case "on_hold":
      return { className: BADGE_TONES.amber, label: "On Hold" };
    case "cancelled":
      return { className: BADGE_TONES.red, label: "Cancelled" };
    default:
      return { className: BADGE_TONES.slate, label: status };
  }
}

/** Human-readable Stripe failure reason */
function formatFailureReason(reason: string): string {
  const map: Record<string, string> = {
    card_declined: "Card declined",
    insufficient_funds: "Insufficient funds",
    expired_card: "Expired card",
    incorrect_cvc: "Incorrect CVC",
    processing_error: "Processing error",
    authentication_required: "Authentication required",
    generic_decline: "Declined",
  };
  return map[reason] ?? reason.replace(/_/g, " ");
}

function useColumns() {
  return React.useMemo<ColumnDef<SalesClientPerformanceRow>[]>(
    () => [
      {
        id: "clientName",
        accessorKey: "clientName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Client" />
        ),
        cell: ({ row }) => (
          <div className="min-w-[180px]">
            <p className="font-medium">
              {row.original.company || row.original.clientName}
            </p>
            {row.original.company &&
            row.original.company !== row.original.clientName ? (
              <p className="text-muted-foreground text-xs">{row.original.clientName}</p>
            ) : null}
            {row.original.email ? (
              <p className="text-muted-foreground text-xs">{row.original.email}</p>
            ) : null}
          </div>
        ),
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const clientName = (row.original.clientName ?? "").toLowerCase();
          const company = (row.original.company ?? "").toLowerCase();
          return clientName.includes(search) || company.includes(search);
        },
        meta: { label: "Client", placeholder: "Search client...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => row.isEnterprise,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Enterprise" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getEnterpriseBadgeClass(row.original.isEnterprise)}
          >
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select",
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some(
            (value) => value === String(Boolean(row.original.isEnterprise)),
          ),
      },
      {
        id: "tier",
        accessorFn: (row) => row.tier ?? 0,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Tier" />
        ),
        cell: ({ row }) =>
          row.original.tier ? (
            <Badge
              variant="outline"
              className={tierBadgeClass(row.original.tier)}
            >
              Tier {row.original.tier}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
        meta: { label: "Tier", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "paymentHealth",
        accessorFn: (row) => {
          if (row.failedCount > 0) return "failed";
          if (row.overdueCount > 0) return "overdue";
          return "current";
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Payment" />
        ),
        cell: ({ row }) => {
          const { className, label } = paymentHealthBadgeClass(row.original);
          const hasIssues =
            row.original.failedCount > 0 || row.original.overdueCount > 0;
          return (
            <div className="flex flex-col gap-0.5">
              <Badge variant="outline" className={className}>
                {label}
              </Badge>
              {hasIssues ? (
                <span className="text-muted-foreground text-[11px]">
                  {row.original.failedCount > 0
                    ? `${row.original.failedCount} failed`
                    : `${row.original.overdueCount} overdue`}
                </span>
              ) : null}
            </div>
          );
        },
        meta: {
          label: "Payment",
          variant: "select",
          options: [
            { label: "Failed", value: "failed" },
            { label: "Overdue", value: "overdue" },
            { label: "Current", value: "current" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) => {
          const health =
            row.original.failedCount > 0
              ? "failed"
              : row.original.overdueCount > 0
                ? "overdue"
                : "current";
          return filterValues.some((v) => v === health);
        },
      },
      {
        id: "unpaidAmount",
        accessorKey: "unpaidAmount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Unpaid" />
        ),
        cell: ({ row }) => {
          const amount = row.original.unpaidAmount;
          if (amount <= 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <span className="font-medium text-red-600 dark:text-red-400">
              {formatCurrency(amount)}
            </span>
          );
        },
        meta: { label: "Unpaid", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalOrders",
        accessorKey: "totalOrders",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Orders" />
        ),
        cell: ({ row }) => row.original.totalOrders.toLocaleString(),
        meta: { label: "Orders", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorKey: "totalBags",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Bags" />
        ),
        cell: ({ row }) => row.original.totalBags.toLocaleString(),
        meta: { label: "Bags", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalRevenue",
        accessorKey: "totalRevenue",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Revenue" />
        ),
        cell: ({ row }) => (
          <span className="font-semibold">{formatCurrency(row.original.totalRevenue)}</span>
        ),
        meta: { label: "Revenue", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "avgOrderValue",
        accessorKey: "avgOrderValue",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Avg Order" />
        ),
        cell: ({ row }) => formatCurrency(row.original.avgOrderValue),
        meta: { label: "Avg Order", variant: "number" },
      },
      {
        id: "last30Revenue",
        accessorKey: "last30Revenue",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Revenue 30d" />
        ),
        cell: ({ row }) => formatCurrency(row.original.last30Revenue),
        meta: { label: "Revenue 30d", variant: "number" },
      },
      {
        id: "previous30Revenue",
        accessorKey: "previous30Revenue",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Prev 30d" />
        ),
        cell: ({ row }) => formatCurrency(row.original.previous30Revenue),
        meta: { label: "Prev 30d", variant: "number" },
      },
      {
        id: "revenueMoMPercent",
        accessorKey: "revenueMoMPercent",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="MoM %" />
        ),
        cell: ({ row }) => {
          const value = row.original.revenueMoMPercent;
          if (value === null) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <Badge
              variant="outline"
              className={momentumBadgeClass(value)}
            >
              {formatPercent(value)}
            </Badge>
          );
        },
        meta: { label: "MoM %", variant: "number" },
      },
      {
        id: "avgMonthlyRevenue",
        accessorKey: "avgMonthlyRevenue",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Avg Rev/Mo" />
        ),
        cell: ({ row }) => formatCurrency(row.original.avgMonthlyRevenue),
        meta: { label: "Avg Rev/Mo", variant: "number" },
      },
      {
        id: "lastOrderAt",
        accessorKey: "lastOrderAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Last Order" />
        ),
        cell: ({ row }) =>
          row.original.lastOrderAt ? (
            <span className="text-sm">
              {formatDate(row.original.lastOrderAt, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
        meta: { label: "Last Order", variant: "date" },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

function ClientOrdersExpandedRow({
  row,
  authUserId,
  dateRange,
  canSendInvoice,
}: {
  row: Row<SalesClientPerformanceRow>;
  authUserId: string;
  dateRange: DateRangeValue;
  canSendInvoice: boolean;
}) {
  const clientOrders = useQuery(
    api.sales.getClientOrdersWithPayments,
    {
      authUserId,
      clientProfileId: row.original.userProfileId,
      dateRange,
    },
  ) as ClientOrderRow[] | undefined;

  const sendInvoiceAction = useAction(api.orders.sendInvoiceEmailAction);
  const [sendingInvoiceIds, setSendingInvoiceIds] = React.useState<Set<string>>(new Set());

  const onSendInvoice = React.useCallback(
    async (order: ClientOrderRow) => {
      setSendingInvoiceIds((prev) => new Set(prev).add(order._id));
      try {
        const result = await sendInvoiceAction({ orderId: order._id as any });
        if ((result as any)?.success) {
          toast.success(
            (result as any)?.sentTo
              ? `Invoice email sent to ${(result as any).sentTo}`
              : "Invoice email sent successfully",
          );
        } else {
          toast.error((result as any)?.error || "Failed to send invoice email");
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to send invoice email",
        );
      } finally {
        setSendingInvoiceIds((prev) => {
          const next = new Set(prev);
          next.delete(order._id);
          return next;
        });
      }
    },
    [sendInvoiceAction],
  );

  if (clientOrders === undefined) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
        <span className="text-muted-foreground ml-2 text-sm">Loading orders...</span>
      </div>
    );
  }

  if (clientOrders.length === 0) {
    return (
      <div className="rounded-md border border-dashed px-4 py-4 text-sm text-muted-foreground">
        No orders found for this client in the selected period.
      </div>
    );
  }

  const gridClass =
    "grid grid-cols-[100px_minmax(0,1fr)_100px_110px_100px_120px_minmax(140px,1fr)_auto] gap-3";

  return (
    <div className="w-full space-y-2 px-4 md:px-6">
      {/* Header row */}
      <div
        className={`${gridClass} rounded-md border bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground`}
      >
        <span>Order #</span>
        <span>Status</span>
        <span>Total</span>
        <span>Payment</span>
        <span>Unpaid</span>
        <span>Due Date</span>
        <span>Details</span>
        <span>Actions</span>
      </div>
      {/* Order rows */}
      <div className="space-y-1">
        {clientOrders.map((order) => {
          const statusBadge = orderStatusBadge(order.status);
          const healthBadge = orderPaymentHealthBadge(order.paymentHealth);
          const hasFailures = order.failureReasons.length > 0;

          return (
            <div
              key={order._id}
              className={`${gridClass} items-center rounded-md px-3 py-2 text-sm ${
                order.paymentHealth === "failed"
                  ? "bg-red-50/50 dark:bg-red-950/20"
                  : order.paymentHealth === "overdue"
                    ? "bg-amber-50/50 dark:bg-amber-950/20"
                    : ""
              }`}
            >
              <span className="font-mono text-xs font-medium">
                {order.orderNumber ?? "-"}
              </span>
              <Badge variant="outline" className={statusBadge.className}>
                {statusBadge.label}
              </Badge>
              <span>{formatCurrency(order.orderTotal)}</span>
              <Badge variant="outline" className={healthBadge.className}>
                {healthBadge.label}
              </Badge>
              <span>
                {order.unpaidAmount > 0 ? (
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {formatCurrency(order.unpaidAmount)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
              <span className="text-xs">
                {order.dueDate
                  ? formatDate(order.dueDate, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "-"}
              </span>
              <div className="flex flex-col gap-0.5">
                {order.paymentTerms ? (
                  <span className="text-muted-foreground text-xs">
                    {order.paymentTerms}
                  </span>
                ) : null}
                {order.oldestOverdueDays !== null && order.oldestOverdueDays > 0 ? (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    {order.oldestOverdueDays}d overdue
                  </span>
                ) : null}
                {hasFailures
                  ? order.failureReasons.map((reason) => (
                      <span
                        key={reason}
                        className="text-xs text-red-600 dark:text-red-400"
                      >
                        {formatFailureReason(reason)}
                      </span>
                    ))
                  : null}
              </div>
              <div>
                {canSendInvoice ? (
                  <Button
                    size="xs"
                    variant="outline"
                    className="h-7 gap-1.5"
                    disabled={sendingInvoiceIds.has(order._id)}
                    onClick={() => onSendInvoice(order)}
                  >
                    <Mail className="size-3.5" />
                    {sendingInvoiceIds.has(order._id) ? "Sending..." : "Send Invoice"}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SalesClientPerformance() {
  const columns = useColumns();
  const { authUserId, userRole, isPending } = useAuthIdentity();
  const [dateRange, setDateRange] = React.useState<DateRangeValue>("90d");
  const [selectedSalesRepId, setSelectedSalesRepId] =
    React.useState<string>("all");
  const isAdmin = userRole === "admin";
  const canSendInvoice = hasPermission(userRole, "sales:edit");

  const analytics = useQuery(
    api.sales.getCustomerAnalytics,
    authUserId
      ? {
          authUserId,
          dateRange,
          ...(isAdmin && selectedSalesRepId !== "all"
            ? { salesRepId: selectedSalesRepId }
            : {}),
        }
      : "skip",
  ) as SalesClientPerformanceResult | undefined;

  const salesRepOptions = analytics?.salesRepOptions ?? [];
  const selectedSalesRepLabel = React.useMemo(() => {
    if (selectedSalesRepId === "all") return "All reps";
    return (
      salesRepOptions.find((option) => option.value === selectedSalesRepId)
        ?.label ?? selectedSalesRepId
    );
  }, [salesRepOptions, selectedSalesRepId]);

  React.useEffect(() => {
    if (!isAdmin) {
      setSelectedSalesRepId("all");
      return;
    }
    if (!analytics) return;
    if (selectedSalesRepId === "all") return;
    if (!salesRepOptions.some((option) => option.value === selectedSalesRepId)) {
      setSelectedSalesRepId("all");
    }
  }, [analytics, isAdmin, salesRepOptions, selectedSalesRepId]);

  const isLoading = analytics === undefined || (isPending && !analytics);
  const rows = analytics?.data ?? [];
  const totals = analytics?.totals;
  const selectedLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === dateRange)?.label ??
    "Selected period";

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base">Client Performance</CardTitle>
            <CardDescription>
              {isAdmin
                ? "Orders and month-over-month revenue trends across all client accounts."
                : "Orders and month-over-month revenue trends for your assigned clients."}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {isAdmin ? (
              <Select
                value={selectedSalesRepId}
                onValueChange={setSelectedSalesRepId}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Select sales rep" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reps</SelectItem>
                  {salesRepOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label} ({option.clientCount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Select
              value={dateRange}
              onValueChange={(value) => setDateRange(value as DateRangeValue)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <DataTableSkeleton columnCount={12} rowCount={8} withPagination />
        ) : rows.length === 0 || !totals ? (
          <div className="flex min-h-[180px] items-center justify-center rounded-md border border-dashed">
            <p className="text-muted-foreground text-sm">
              No client order activity found for {selectedLabel}.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <InternalMetricCard
                title="Clients"
                value={totals.clientCount.toLocaleString()}
                hint={`${totals.assignedClientCount.toLocaleString()} assigned`}
                icon={Users}
              />
              <InternalMetricCard
                title="Total Revenue"
                value={formatCurrency(totals.totalRevenue)}
                hint={selectedLabel}
                icon={DollarSign}
              />
              <InternalMetricCard
                title="Orders"
                value={totals.orderCount.toLocaleString()}
                hint={selectedLabel}
                icon={TrendingUp}
              />
              <InternalMetricCard
                title="Bags Sold"
                value={totals.totalBags.toLocaleString()}
                hint={selectedLabel}
                icon={Package}
              />
              <InternalMetricCard
                title="Revenue 30d"
                value={formatCurrency(totals.last30Revenue)}
                hint={`MoM ${formatPercent(totals.revenueMoMPercent)}`}
                icon={TrendingUp}
              />
              <InternalMetricCard
                title="Unpaid"
                value={formatCurrency(totals.totalUnpaidAmount)}
                hint={`${totals.totalUnpaidClients} client${totals.totalUnpaidClients === 1 ? "" : "s"}`}
                icon={AlertTriangle}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="border-border/80 bg-muted/30 text-foreground"
              >
                {selectedLabel}
              </Badge>
              {isAdmin ? (
                <Badge
                  variant="outline"
                  className="border-border/80 bg-muted/30 text-foreground"
                >
                  {selectedSalesRepLabel}
                </Badge>
              ) : null}
              <Badge
                variant="outline"
                className="border-border/80 bg-muted/30 text-foreground"
              >
                {rows.length.toLocaleString()} clients with orders
              </Badge>
            </div>

            <InternalEntityDataTable
              data={rows}
              columns={columns}
              pageSize={20}
              enableSelection={false}
              expandedRow={{
                render: ({ row }) => (
                  <ClientOrdersExpandedRow
                    row={row}
                    authUserId={authUserId!}
                    dateRange={dateRange}
                    canSendInvoice={canSendInvoice}
                  />
                ),
                getCanExpand: (row) =>
                  row.totalOrders > 0,
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
